import { useCallback, useEffect, useState } from 'react';
import { getApplicants, getApplicant, updateApplicantStatus, initiateRefund } from '../../services/applicantsService';
import { getErrorMessage } from '../../api/client';
import Modal from './Modal';

const getPaymentStatusClass = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'paid') return 'paid';
  if (normalized === 'failed') return 'failed';
  if (normalized === 'pending') return 'pending';
  return 'unknown';
};

const defaultFilters = {
  search: '',
  status: '',
  payment_status: '',
  age_min: '',
  age_max: ''
};

const ApplicantsTab = () => {
  const [applicants, setApplicants] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [filters, setFilters] = useState(defaultFilters);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [actionError, setActionError] = useState('');
  const [isActionRunning, setIsActionRunning] = useState(false);

  const fetchApplicants = useCallback(async (page = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const result = await getApplicants({ ...filters, page, limit: pagination.limit });
      setApplicants(result.data);
      setPagination({
        page: result.pagination?.page || page,
        limit: result.pagination?.limit || pagination.limit,
        total: result.pagination?.total || 0
      });
    } catch (fetchError) {
      setError(getErrorMessage(fetchError, 'Unable to load applicants.'));
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.limit]);

  useEffect(() => {
    fetchApplicants(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));

  const openDetail = async (id) => {
    setDetailError('');
    setActionMessage('');
    setActionError('');
    setIsDetailLoading(true);
    setSelectedApplicant({ id });
    try {
      const result = await getApplicant(id);
      setSelectedApplicant(result.data);
    } catch (fetchError) {
      setDetailError(getErrorMessage(fetchError, 'Unable to load applicant details.'));
    } finally {
      setIsDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedApplicant(null);
  };

  const handleStatusUpdate = async (id, status) => {
    setIsActionRunning(true);
    setActionError('');
    setActionMessage('');
    try {
      await updateApplicantStatus(id, status, status === 'rejected' ? 'Rejected by admin' : 'Approved by admin');
      setActionMessage(`Applicant marked as ${status}.`);
      await openDetail(id);
      fetchApplicants(pagination.page);
    } catch (updateError) {
      setActionError(getErrorMessage(updateError, 'Unable to update status.'));
    } finally {
      setIsActionRunning(false);
    }
  };

  const handleRefund = async (id) => {
    setIsActionRunning(true);
    setActionError('');
    setActionMessage('');
    try {
      const result = await initiateRefund(id, 'Refund initiated by admin');
      setActionMessage(result?.message || 'Refund initiated.');
      await openDetail(id);
      fetchApplicants(pagination.page);
    } catch (refundError) {
      setActionError(getErrorMessage(refundError, 'Unable to initiate refund.'));
    } finally {
      setIsActionRunning(false);
    }
  };

  return (
    <div>
      <div className="row g-3 align-items-end mb-3">
        <div className="col-12 col-lg-3">
          <label className="form-label text-white fw-bold">Search</label>
          <input
            type="search"
            name="search"
            className="form-control theme-field px-3"
            placeholder="Name or email"
            value={filters.search}
            onChange={handleFilterChange}
          />
        </div>
        <div className="col-6 col-lg-2">
          <label className="form-label text-white fw-bold">Status</label>
          <select name="status" className="form-select theme-field px-3" value={filters.status} onChange={handleFilterChange}>
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="payment_pending">Payment Pending</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div className="col-6 col-lg-2">
          <label className="form-label text-white fw-bold">Payment</label>
          <select name="payment_status" className="form-select theme-field px-3" value={filters.payment_status} onChange={handleFilterChange}>
            <option value="">All</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div className="col-6 col-lg-2">
          <label className="form-label text-white fw-bold">Age Min</label>
          <input type="number" name="age_min" className="form-control theme-field px-3" value={filters.age_min} onChange={handleFilterChange} />
        </div>
        <div className="col-6 col-lg-2">
          <label className="form-label text-white fw-bold">Age Max</label>
          <input type="number" name="age_max" className="form-control theme-field px-3" value={filters.age_max} onChange={handleFilterChange} />
        </div>
        <div className="col-12 col-lg-1">
          <span className="theme-mini-card admin-panel-count d-flex align-items-center justify-content-center px-2 py-2">
            {pagination.total}
          </span>
        </div>
      </div>

      {isLoading && <div className="theme-state text-center p-3">Loading applicants...</div>}
      {!isLoading && error && <div className="theme-state theme-error text-center p-3">{error}</div>}
      {!isLoading && !error && applicants.length === 0 && (
        <div className="theme-state text-center p-3">No applicants found.</div>
      )}

      {!isLoading && !error && applicants.length > 0 && (
        <>
          <div className="admin-panel-table-wrap">
            <table className="table table-hover mb-0 admin-panel-table">
              <thead>
                <tr>
                  <th>Sr No.</th>
                  <th>Name</th>
                  <th>Age</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Payment</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {applicants.map((applicant, index) => (
                  <tr key={applicant.id}>
                    <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                    <td>{applicant.name}</td>
                    <td>{applicant.age}</td>
                    <td>{applicant.email}</td>
                    <td>{applicant.phone}</td>
                    <td>
                      <span className={`theme-status ${getPaymentStatusClass(applicant.status)}`}>{applicant.status}</span>
                    </td>
                    <td>
                      <span className={`theme-status ${getPaymentStatusClass(applicant.payment_status)}`}>{applicant.payment_status}</span>
                    </td>
                    <td>
                      <div className="admin-panel-actions">
                        <button type="button" className="admin-action-btn" onClick={() => openDetail(applicant.id)}>
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="admin-panel-pagination mt-3">
            <button
              type="button"
              className="btn theme-btn theme-btn-outline px-3"
              disabled={pagination.page <= 1}
              onClick={() => fetchApplicants(pagination.page - 1)}
            >
              Previous
            </button>
            <span className="text-white">
              Page {pagination.page} of {totalPages}
            </span>
            <button
              type="button"
              className="btn theme-btn theme-btn-outline px-3"
              disabled={pagination.page >= totalPages}
              onClick={() => fetchApplicants(pagination.page + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}

      {selectedApplicant && (
        <Modal title="Applicant Details" onClose={closeDetail}>
          {isDetailLoading && <div className="theme-state text-center p-3">Loading...</div>}
          {!isDetailLoading && detailError && <div className="theme-state theme-error text-center p-3">{detailError}</div>}

          {!isDetailLoading && !detailError && selectedApplicant.name && (
            <div>
              <div className="admin-detail-grid mb-3">
                <div className="admin-detail-item"><span className="label">Name</span><span className="value">{selectedApplicant.name}</span></div>
                <div className="admin-detail-item"><span className="label">Age</span><span className="value">{selectedApplicant.age}</span></div>
                <div className="admin-detail-item"><span className="label">Email</span><span className="value">{selectedApplicant.email}</span></div>
                <div className="admin-detail-item"><span className="label">Phone</span><span className="value">{selectedApplicant.phone}</span></div>
                <div className="admin-detail-item"><span className="label">Home Town</span><span className="value">{selectedApplicant.home_town}</span></div>
                <div className="admin-detail-item"><span className="label">Present Location</span><span className="value">{selectedApplicant.present_location}</span></div>
                <div className="admin-detail-item"><span className="label">Height (cm)</span><span className="value">{selectedApplicant.height_cm}</span></div>
                <div className="admin-detail-item"><span className="label">Weight (kg)</span><span className="value">{selectedApplicant.weight_kg}</span></div>
                <div className="admin-detail-item"><span className="label">Chest</span><span className="value">{selectedApplicant.chest_inches}"</span></div>
                <div className="admin-detail-item"><span className="label">Waist</span><span className="value">{selectedApplicant.waist_inches}"</span></div>
                <div className="admin-detail-item"><span className="label">Hip</span><span className="value">{selectedApplicant.hip_inches}"</span></div>
                <div className="admin-detail-item"><span className="label">Shoe Size</span><span className="value">{selectedApplicant.shoe_size}</span></div>
                <div className="admin-detail-item"><span className="label">Skin Tone</span><span className="value">{selectedApplicant.skin_tone}</span></div>
                <div className="admin-detail-item"><span className="label">Eye Colour</span><span className="value">{selectedApplicant.eye_colour}</span></div>
                <div className="admin-detail-item"><span className="label">Hair Colour</span><span className="value">{selectedApplicant.hair_colour}</span></div>
                <div className="admin-detail-item"><span className="label">Father Occupation</span><span className="value">{selectedApplicant.father_occupation}</span></div>
                <div className="admin-detail-item"><span className="label">Marital Status</span><span className="value">{selectedApplicant.marital_status}</span></div>
                <div className="admin-detail-item">
                  <span className="label">Status</span>
                  <span className={`theme-status ${getPaymentStatusClass(selectedApplicant.status)}`}>{selectedApplicant.status}</span>
                </div>
                <div className="admin-detail-item">
                  <span className="label">Payment Status</span>
                  <span className={`theme-status ${getPaymentStatusClass(selectedApplicant.payment_status)}`}>{selectedApplicant.payment_status}</span>
                </div>
              </div>

              <h5 className="text-white mt-4 mb-2">Payment Logs</h5>
              {selectedApplicant.paymentLogs?.length ? (
                <ul className="list-unstyled mb-3">
                  {selectedApplicant.paymentLogs.map((log) => (
                    <li key={log.id} className="theme-mini-card p-2 mb-2">
                      <span className="text-white">{log.event_type}</span>
                      {' — '}
                      <span className="text-white-50">{log.status}</span>
                      {log.amount ? <span className="text-white-50"> — ₹{(log.amount / 100).toLocaleString('en-IN')}</span> : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="theme-copy">No payment logs available.</p>
              )}

              {actionMessage && <div className="submission-message success">{actionMessage}</div>}
              {actionError && <div className="submission-message error">{actionError}</div>}

              <div className="admin-panel-actions mt-3">
                <button
                  type="button"
                  className="admin-action-btn approve"
                  disabled={isActionRunning || selectedApplicant.status === 'active'}
                  onClick={() => handleStatusUpdate(selectedApplicant.id, 'active')}
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="admin-action-btn reject"
                  disabled={isActionRunning || selectedApplicant.status === 'rejected'}
                  onClick={() => handleStatusUpdate(selectedApplicant.id, 'rejected')}
                >
                  Reject
                </button>
                <button
                  type="button"
                  className="admin-action-btn refund"
                  disabled={isActionRunning || selectedApplicant.payment_status !== 'paid'}
                  onClick={() => handleRefund(selectedApplicant.id)}
                >
                  Initiate Refund
                </button>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

export default ApplicantsTab;
